'use client';
import { Container, Row, Col } from 'react-bootstrap';
import { PageHeader } from './components/PageHeader';
import { FilterSection } from './components/FilterSection';
import { StatusAlert } from './components/StatusAlert';
import { LoadingOverlay } from './components/LoadingOverlay';
import { ProductList } from './components/ProductList';
import CompetitorSelectionSection from './components/CompetitorSelectionSection';
import { useMatchSku } from '../../../apps/hooks/competitor/useMatchSku';
import { CompetitorClient } from '../../../infrastructure/CompetitorClient';
import { getConfig } from '../../../config';
import type { ProductData } from './models/types';

export default function MatchSKUPage() {
  const config = getConfig();
  const competitorClient = new CompetitorClient(config.apiUrl);

  // Use our enhanced custom hook
  const {
    matchedProductsQuery,
    countTotalMatches,
    selectedProduct,
    activeCompetitor,
    selectedProducts,
    isProductSelected,
    error,
    filterName,
    setFilterName,
    filterBrand,
    setFilterBrand,
    filterCategory,
    setFilterCategory,
    handleProductSelect,
    handleCompetitorProductSelect,
    getFilteredProducts,
    setActiveCompetitor,
  } = useMatchSku();

  const updateSelectedProduct = (updatedProduct: ProductData) => {
    // Find the updated product in the current list and replace it
    if (selectedProduct && selectedProduct.skinSeoulProduct.sku === updatedProduct.skinSeoulProduct.sku) {
      handleProductSelect(updatedProduct);
    }
  };

  // Access products and loading state from the query
  const { data: products = [], isLoading: loading } = matchedProductsQuery;

  // Get filtered products
  const filteredProducts = () => getFilteredProducts(products, filterName, filterBrand, filterCategory);

  return (
    <Container
      fluid
      className="py-4 bg-light position-relative"
      style={{
        height: 'calc(100vh - 60px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <LoadingOverlay loading={loading} />

      <PageHeader title="Product Matching" />

      <StatusAlert message={error} />

      <FilterSection
        filterName={filterName}
        filterBrand={filterBrand}
        filterCategory={filterCategory}
        setFilterName={setFilterName}
        setFilterBrand={setFilterBrand}
        setFilterCategory={setFilterCategory}
        loading={loading}
      />

      <Row className="g-4 flex-grow-1" style={{ overflow: 'hidden', minHeight: 0 }}>
        <ProductList
          filteredProducts={filteredProducts()}
          selectedProduct={selectedProduct}
          countTotalMatches={countTotalMatches}
          handleProductSelect={handleProductSelect}
          selectedProducts={selectedProducts}
          loading={loading}
        />

        <Col md={8} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {selectedProduct ? (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CompetitorSelectionSection
                selectedProduct={selectedProduct}
                activeCompetitor={activeCompetitor}
                selectedProducts={selectedProducts}
                handleCompetitorProductSelect={handleCompetitorProductSelect}
                isProductSelected={isProductSelected}
                setActiveCompetitor={setActiveCompetitor}
                competitorClient={competitorClient}
                onProductDataUpdate={updateSelectedProduct}
              />
            </div>
          ) : (
            <div
              className="bg-white rounded-3 p-4 text-center d-flex align-items-center justify-content-center"
              style={{
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                height: '100%',
              }}
            >
              <div className="text-muted">Select a product to see competitor matches</div>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
}
